// Types and labels for the service board and the damage register.
//
// No arithmetic lives here. Every kilometre figure on the page is computed in
// v_vehicle_service_board, which is the same view the nightly alert reads, so
// the screen and the email cannot disagree.
import type { ChipTone } from '../components/interviews/Chip'

export type DueState = 'overdue' | 'due_now' | 'due_soon' | 'no_baseline' | 'ok'

export type ServiceBoardRow = {
  vehicle_id: string
  plate: string | null
  vehicle_name: string | null
  due_state: DueState
  due_rank: number
  manager_person_id: string | null
  manager_name: string | null
  manager_email: string | null
  owner_name: string | null
  interval_km: number
  alert_lead_km: number
  current_km: number | null
  odometer_observed_at: string | null
  odometer_source: string | null
  reading_age_days: number | null
  last_serviced_on: string | null
  baseline_km: number | null
  baseline_provisional: boolean | null
  baseline_has_stamp: boolean | null
  km_since_service: number | null
  due_at_km: number | null
  km_to_service: number | null
  open_booking_id: string | null
  open_booking_status: string | null
  booked_for: string | null
  booked_provider: string | null
  open_damage_items: number
  damage_reported_new: number
  damage_timing_unknown: number
  oldest_open_damage_at: string | null
  open_damage_sections: string | null
  last_inspected_at: string | null
  inspection_count: number
}

export type DamageRow = {
  id: string
  ref: string | null
  vehicle_id: string
  plate: string | null
  section: string
  section_label: string
  damage_type: string | null
  description: string | null
  status: string
  liability_route: string | null
  first_seen_at: string
  first_attribution: string | null
  last_seen_at: string | null
  sightings: number
  task_id: string | null
  repair_case_id: string | null
  notes: string | null
  amount_incl_vat_zar: number | null
  dealer_name: string | null
  quote_date: string | null
}

export const DUE_STATE_LABEL: Record<DueState, string> = {
  overdue: 'Overdue',
  due_now: 'Due now',
  due_soon: 'Due soon',
  no_baseline: 'Cannot be assessed',
  ok: 'Inside the interval',
}

export function dueTone(state: DueState): ChipTone {
  if (state === 'overdue') return 'red'
  if (state === 'due_now') return 'amber'
  if (state === 'due_soon') return 'brand'
  if (state === 'no_baseline') return 'neutral'
  return 'green'
}

/**
 * The position in words, which is what a manager reads first.
 *
 * "Cannot be assessed" is not a tidier way of saying nothing is wrong. It is the
 * absence of a last service odometer, and it is reported as its own state rather
 * than folded into ok, because a silent car is the one that gets missed.
 */
export function duePhrase(r: ServiceBoardRow): string {
  if (r.due_state === 'no_baseline') return 'No last service odometer on file'
  if (r.km_to_service === null) return 'Not known'
  if (r.km_to_service <= 0) return `${km(Math.abs(r.km_to_service))} past its service`
  return `${km(r.km_to_service)} to go`
}

export function km(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${Math.round(value).toLocaleString('en-ZA')} km`
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
}

/**
 * Why a figure on this row may not be what the car actually reads.
 *
 * Returned as a list rather than a sentence so the screen can show every reason,
 * not the first one. A provisional baseline and a stale odometer are two separate
 * admissions and a manager deciding whether to pull a car off the road is owed both.
 */
export function caveats(r: ServiceBoardRow): string[] {
  const out: string[] = []
  if (r.baseline_provisional) {
    out.push('Last service odometer is inferred, not read off the service book')
  }
  if (r.reading_age_days !== null && r.reading_age_days > 14) {
    out.push(`Odometer reading is ${r.reading_age_days} days old, so the car has run further`)
  }
  if (r.current_km === null) {
    out.push('No usable odometer reading for this vehicle at all')
  }
  return out
}

/**
 * Where the board's current odometer came from, in words.
 *
 * cartrack_projected is the last dashboard reading plus the distance the car's
 * tracker logged since (v_vehicle_odometer_current, migration 2862). It is
 * labelled so nobody reads it as a figure off the dashboard: the tracker's own
 * odometer does not match the dashboard, only its distance is used.
 */
export function odometerSourceLabel(source: string | null | undefined): string | null {
  if (!source) return null
  if (source === 'inspection') return 'driver inspection'
  if (source === 'cartrack_projected') return 'carried forward by Cartrack'
  if (source === 'manual') return 'entered by staff'
  if (source === 'service_record') return 'service record'
  return source.replace(/_/g, ' ')
}

/**
 * An odometer as a person types it: "152 011", "152,011" or "152011".
 * Returns null for anything that is not a whole number, so the button stays off
 * rather than sending something the database will refuse.
 */
export function kmEntry(raw: string): number | null {
  const digits = raw.replace(/[\s ,]/g, '')
  if (!/^\d{1,7}$/.test(digits)) return null
  return Number(digits)
}

export function attributionLabel(a: string | null | undefined): string {
  if (a === 'new_since_last_shift') return 'New since the last shift'
  if (a === 'existing') return 'Already there before'
  if (a === 'not_sure') return 'Driver could not say when'
  return 'Not recorded'
}

export function attributionTone(a: string | null | undefined): ChipTone {
  if (a === 'new_since_last_shift') return 'amber'
  if (a === 'existing') return 'neutral'
  return 'neutral'
}
